import { isApiError } from '@platform/api-client';

/**
 * The sentence to show for a failure (M2.8, API.md §1.6, SECURITY.md §4.4).
 *
 * The API writes its own messages to be shown, so this mostly gets out of the way. Two
 * codes get more than the sentence:
 *
 * - **`RATE_LIMITED`** carries `Retry-After`, and "try again in 15 minutes" is far more
 *   useful than "too many attempts" alone. A locked account answers the *same* `429` on
 *   purpose, because "this account is locked" confirms the account exists - so the word
 *   "locked" appears nowhere in this file and must appear nowhere in the console.
 * - **`FORBIDDEN` on the sign-in screen**, where it means a real account that is not an
 *   Object Labs one. `describeSignInError` says so plainly rather than leaving someone
 *   retyping a password that is already correct.
 */
export function describeApiError(error: unknown): string {
  if (typeof error === 'string' && error.trim() !== '') return error;

  if (isApiError(error)) {
    if (error.code === 'RATE_LIMITED' && error.retryAfter !== undefined) {
      return `${error.message} You can try again in ${formatSeconds(error.retryAfter)}.`;
    }
    return error.message;
  }
  /*
   * A seam error, not any error (SR-54). `lib/api/*` each refuse some inputs before the
   * server sees them, and each throws its own `<Thing>ClientError` carrying a `code` and
   * a sentence written for this screen - the same contract `ApiError` has. Those are
   * shown.
   *
   * **A bare `Error` is not.** Its `message` was written by nobody for anybody to read:
   * a thrown render, a bundler's own wording, a `TypeError` naming whatever data tripped
   * it. Only messages written to be displayed are displayed (API.md §1.6, SECURITY.md
   * §4.4); everything else gets the sentence below. The patient auth helper always did
   * this; the other three drifted, which is the half of SR-54 that is about drift.
   */
  if (isSeamError(error)) return error.message;
  return 'Something went wrong. Please try again.';
}

/** `describeApiError`, plus the one thing only the sign-in screen knows how to say. */
export function describeSignInError(error: unknown): string {
  if (isApiError(error) && error.code === 'FORBIDDEN') {
    return 'This console is for Object Labs staff. Clinic accounts sign in through their own admin app.';
  }
  return describeApiError(error);
}

/**
 * One of `lib/api/*`'s `<Thing>ClientError`s: a client-side refusal with a `code` and a
 * message meant for the screen. The name is set as a string literal in each constructor,
 * so it survives minification, and `code` is what separates them from a bare `Error`
 * somebody happened to name the same way.
 */
function isSeamError(error: unknown): error is Error & { code: string } {
  return (
    error instanceof Error &&
    error.name.endsWith('ClientError') &&
    typeof (error as { code?: unknown }).code === 'string' &&
    error.message !== ''
  );
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${String(seconds)} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
}
